"use client";

import { m, type Variants } from "motion/react";
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { revealUp } from "./variants";

interface RevealOnScrollProps<T extends ElementType = "div"> {
  children: ReactNode;
  /** Motion variants to use. Defaults to `revealUp`. */
  variants?: Variants;
  /** Render-as element. Defaults to `div`. */
  as?: T;
  /** Delay before the reveal kicks in (seconds). */
  delay?: number;
  /** How much of the element must be in view before triggering. 0-1. */
  amount?: number;
  /** Trigger once (default) or every time it enters the viewport. */
  once?: boolean;
  className?: string;
}

/**
 * Declarative in-view reveal. The default behaviour is the site's
 * standard "fade-up" — pass a different `variants` to override.
 *
 * Why a wrapper instead of inlining `motion.div whileInView=...` everywhere:
 * - Single place to tweak threshold / once-behaviour for the whole site.
 * - Motion respects `prefers-reduced-motion` automatically via its
 *   `MotionConfig` — see `app/layout.tsx`.
 */
export function RevealOnScroll<T extends ElementType = "div">({
  children,
  variants = revealUp,
  as,
  delay,
  amount = 0.2,
  once = true,
  className,
  ...rest
}: RevealOnScrollProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof RevealOnScrollProps<T>>) {
  const Component = (m[(as ?? "div") as keyof typeof m] ??
    m.div) as typeof m.div;

  return (
    <Component
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount }}
      variants={variants}
      transition={delay !== undefined ? { delay } : undefined}
      {...rest}
    >
      {children}
    </Component>
  );
}
